import { useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import CreditApplicationsReports from '../../suppliers/SupplierReporting/CreditApplicationsReports/CreditApplicationsReports';
import CreditReviewerPerformanceTable from './CreditReviewerPerformanceTable';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import { DillSelect, DillDropDownFilter, DillCheckbox } from '../../../general/Dill';

const CreditApplicationsDashboard = () => {
  const { supplierCreditApplicationsAnnualReport } = useAppSelector((state) => state.reports);
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);

  const [selectedYear, setSelectedYear] = useState(`${new Date().getFullYear()}`);
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] } | null>(null);
  const [approvedDateRange, setApprovedDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  });

  const yearsList = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 5; i++) {
      years.push(`${currentYear - i}`);
    }
    return years;
  }, []);

  const filters = useMemo(() => {
    const branchIds =
      supplierCreditApplicationsAnnualReport?.compiledCreditApplicationSummary
        .filter((item) => item.branchId)
        .map((item) => item.branchId) ?? [];
    const uniqueBanchIds = Array.from(new Set(branchIds));
    const salesRepIds =
      supplierCreditApplicationsAnnualReport?.compiledCreditApplicationSummary
        .filter((item) => item.assignedSalesRep)
        .flatMap((item) => item.assignedSalesRep) ?? [];
    const uniqueSalesRepIds = Array.from(new Set(salesRepIds));

    const branches = supplierBranches;

    const filterBranch = branches
      .filter((branch) => uniqueBanchIds.includes(branch.branchId))
      .map((branch) => {
        return { label: branch.branchName, value: branch.branchId };
      });
    const salesReps = branches
      .filter((item) => item.salesRep)
      .flatMap((branch) => branch.salesRep)
      .filter((salesRep) => uniqueSalesRepIds.includes(salesRep?.id))
      .filter((salesRep, index, self) => index === self.findIndex((s) => s?.id === salesRep?.id))
      .map((salesRep) => {
        return { label: salesRep?.name ?? '', value: salesRep?.id ?? '', extra: salesRep?.email };
      });

    return { branches: filterBranch, salesReps };
  }, [supplierCreditApplicationsAnnualReport, user, currentSupplier?.id, supplierBranches]);

  const approvedDateActiveLabel = useMemo(() => {
    if (!approvedDateRange.start && !approvedDateRange.end) return undefined;
    const fmt = (d: string) => (d ? moment(d, 'YYYY-MM-DD').format('MM/DD/YYYY') : '...');
    return `Approved: ${fmt(approvedDateRange.start)} – ${fmt(approvedDateRange.end)}`;
  }, [approvedDateRange]);

  return (
    <div className="flex flex-col w-full h-full">
      <div className="sticky top-0 bg-white z-30 border-b border-gray-200">
        <div className="px-4 sm:px-6">
          <div className="flex flex-row sm:justify-between sm:items-center gap-4 pt-4 pb-1">
            <div className="flex gap-4 flex-row items-center">
              <div className="w-[300px]">
                <DillDropDownFilter
                  placeholder="Filters"
                  closeMenu={false}
                  isMulti
                  showSelectedItems
                  multiValue={selectedFilters}
                  items={[
                    ...(filters.branches.length > 0
                      ? [
                          {
                            id: 'branchId',
                            label: 'Filter by branch',
                            value: filters.branches.map((item) => ({
                              label: item.label ?? '',
                              value: item.value,
                              labelInElement: (
                                <div className="flex mx-2 my-1 items-center gap-2">
                                  <DillCheckbox
                                    isChecked={
                                      selectedFilters?.branchId?.includes(item.value) ?? false
                                    }
                                  />
                                  <div className="text-black text-sm">{item.label}</div>
                                </div>
                              ),
                            })),
                          },
                        ]
                      : []),
                    ...(filters.salesReps.length > 0
                      ? [
                          {
                            id: 'assignedSalesRep',
                            label: 'Filter by sales rep',
                            value: filters.salesReps.map((item) => {
                              const splitLabel = item?.label.split(' ').filter(Boolean);
                              const label =
                                splitLabel.length >= 2
                                  ? `${splitLabel[0][0] ?? ''}${splitLabel[1][0] ?? ''}`
                                  : `${splitLabel[0]?.[0] ?? ''}`;

                              return {
                                label: item.label ?? '',
                                value: item.value,
                                labelInElement: (
                                  <div className="flex mx-2 my-1 items-center gap-2">
                                    <div>
                                      <DillCheckbox
                                        isChecked={
                                          selectedFilters?.assignedSalesRep?.includes(
                                            item?.value
                                          ) ?? false
                                        }
                                      />
                                    </div>
                                    <div className="flex items-center text-sm cursor-pointer">
                                      <div>
                                        <span className="h-8 w-8 flex items-center justify-center rounded-full bg-blue-500 text-white mr-2 text-xs">
                                          {label}
                                        </span>
                                      </div>
                                      <div className="flex flex-col text-black">
                                        <span>{item.label}</span>
                                        <span className="text-xs text-gray-500">
                                          {item?.extra || ''}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ),
                              };
                            }),
                          },
                        ]
                      : []),
                    {
                      id: 'approvedDate',
                      label: 'Date Approved',
                      value: [],
                      activeLabel: approvedDateActiveLabel,
                      customContent: (
                        <div className="flex flex-col gap-2 pt-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-8">From</span>
                            <input
                              type="date"
                              value={approvedDateRange.start}
                              onChange={(e) =>
                                setApprovedDateRange((prev) => ({
                                  ...prev,
                                  start: e.target.value,
                                }))
                              }
                              className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-8">To</span>
                            <input
                              type="date"
                              value={approvedDateRange.end}
                              min={approvedDateRange.start || undefined}
                              onChange={(e) =>
                                setApprovedDateRange((prev) => ({ ...prev, end: e.target.value }))
                              }
                              className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                          </div>
                          {(approvedDateRange.start || approvedDateRange.end) && (
                            <button
                              onClick={() => setApprovedDateRange({ start: '', end: '' })}
                              className="text-xs text-gray-400 hover:text-gray-600 text-left mt-0.5">
                              Clear dates
                            </button>
                          )}
                        </div>
                      ),
                    },
                  ]}
                  showMinimalDesign
                  onMultiSelectChange={(data) => {
                    if (data) {
                      const transformed = Object.fromEntries(
                        Object.entries(data).map(([key, arr]) => [
                          key,
                          (arr as { value: string }[]).map((item) => item.value),
                        ])
                      );
                      setSelectedFilters(transformed);
                    }
                  }}
                />
              </div>
              <div className="min-w-[120px] sm:min-w-[140px]">
                <DillSelect
                  value={selectedYear}
                  options={yearsList.map((year) => ({ label: year, value: year }))}
                  onChange={(value) => {
                    if (yearsList.includes(value)) {
                      setSelectedYear(value);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col w-full h-full p-4 pb-24 gap-4 overflow-scroll">
        <CreditApplicationsReports
          selectedYear={selectedYear}
          filters={selectedFilters}
          approvedDateRange={approvedDateRange}
        />
        <CreditReviewerPerformanceTable />
      </div>
    </div>
  );
};

export default CreditApplicationsDashboard;
